import React, { useContext, useLayoutEffect, useState } from 'react';
import { Router } from 'react-router-dom';
import { Location } from 'history';
import { GlobalConfigurationContext } from '../../lib/context/GlobalConfigurationContext';
import Analytics from '../../tools/Analytics';
import useHistory from '../../lib/hook/useBrowserHistory';

interface Props {
  children: React.ReactNode | ((location: Location) => JSX.Element);
}

const AppRouter: React.FC<Props> = (props: Props): JSX.Element => {
  const { allowStandaloneRouting } = useContext(GlobalConfigurationContext);
  const history = useHistory();

  const { children } = props;

  const [location, setLocation] = useState(history.location);

  /**
   * Subscribes to browser history changes to track the page views. Since
   * the first page when the user lands is not actually a history change
   * (because it's subscribed after the user landed on the page), then the
   * first time the app is loaded, a page view of the current location will be tracked
   */
  useLayoutEffect(() => {
    Analytics.trackPageView(history.location);

    // Keep track of the last pathname reached
    let lastPathname = history.location.pathname;

    const unlisten = history.listen(({ location }) => {
      setLocation(location);
      // Do not fire a pageview event on changing the search params
      if (lastPathname !== location.pathname) {
        lastPathname = location.pathname;
        Analytics.trackPageView(location);
      }
    });

    return () => {
      unlisten();
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const renderChildren = () =>
    typeof children === 'function' ? children(location) : children;

  if (!allowStandaloneRouting) {
    return renderChildren();
  }

  return (
    <Router location={location} navigator={history}>
      {renderChildren()}
    </Router>
  );
};

export default AppRouter;
